import React, { useState, useEffect } from 'react';
import { mockPayrollRuns, mockEmployees } from '../mockData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Play, CheckCircle, Download, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { financeService } from '../services/financeService';
import { toast } from '../hooks/use-toast';
import { useLoading } from '../context/LoadingContext';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const currentYearNow = new Date().getFullYear();
const AVAILABLE_YEARS = Array.from(
  new Set([2024, 2025, 2026, 2027, currentYearNow - 1, currentYearNow, currentYearNow + 1])
).sort((a, b) => a - b);

const Payroll = () => {
  const { user } = useAuth();
  const { startLoading, stopLoading } = useLoading();
  const [payrollRuns, setPayrollRuns] = useState(mockPayrollRuns);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Default to current month and year
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());

  const totalGross = mockEmployees.reduce((sum, emp) => sum + emp.grossSalary, 0);
  const totalNet = mockEmployees.reduce((sum, emp) => sum + emp.netSalary, 0);
  const totalDeductions = totalGross - totalNet;

  // Fetch salary payments on page load (response not consumed yet)
  useEffect(() => {
    const orgId = user?.org_id || user?.orgId;
    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();

    startLoading('Loading payroll data...');
    financeService
      .getSalaryPayments({ orgId, month, year })
      .then((data) => {
        console.log('Salary payments response:', data);
        // TODO: consume response data
      })
      .catch((err) => {
        console.error('Failed to fetch salary payments:', err);
      })
      .finally(() => {
        stopLoading();
      });
  }, [user, startLoading, stopLoading]);

  const handleApproveAndDisburse = async () => {
    setIsSubmitting(true);
    try {
      const orgId = user?.org_id || user?.orgId || 10;
      const response = await financeService.generateSalaryBatch({
        orgId,
        month: selectedMonth,
        year: selectedYear,
      });

      const processedCount = response?.generatedCount ?? response?.employeeCount ?? mockEmployees.length;
      toast({
        title: "Payroll Disbursed Successfully",
        description: `Batch salary successfully generated and disbursed for ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}.${processedCount ? ` Processed: ${processedCount} employees.` : ''}`,
      });

      // Append new run to Payroll History table
      const newRun = {
        id: `PR-${selectedYear}-${String(selectedMonth).padStart(2, '0')}`,
        month: `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`,
        period: `1st - ${new Date(selectedYear, selectedMonth, 0).getDate()}th ${MONTH_NAMES[selectedMonth - 1]}`,
        totalEmployees: processedCount,
        grossAmount: totalGross,
        totalDeductions: totalDeductions,
        netAmount: totalNet,
        disbursedDate: new Date().toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        status: 'Completed',
      };
      setPayrollRuns((prev) => [newRun, ...prev.filter((r) => r.id !== newRun.id)]);

      setIsDialogOpen(false);
    } catch (error) {
      console.error("Batch salary disbursal failed:", error);
      toast({
        title: "Payroll Disbursal Failed",
        description:
          error?.userMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to approve and disburse batch salary. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = () => {
    toast({
      title: "Draft Saved",
      description: `Payroll draft for ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear} has been saved locally.`,
    });
    setIsDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payroll Management</h1>
          <p className="text-gray-500 mt-1">Process and manage monthly payroll</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Play className="mr-2 h-4 w-4" />
              Run New Payroll
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader className="flex flex-row items-center justify-between pb-2 border-b">
              <div>
                <DialogTitle className="text-xl">
                  Run Payroll - {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
                </DialogTitle>
                <p className="text-xs text-gray-500 mt-0.5">
                  Review employee payouts and generate batch salary
                </p>
              </div>
              <div className="flex items-center space-x-2 mr-6">
                <Select
                  value={String(selectedMonth)}
                  onValueChange={(val) => setSelectedMonth(Number(val))}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="w-32 h-8 text-xs bg-white">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_NAMES.map((name, index) => (
                      <SelectItem key={index + 1} value={String(index + 1)}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={String(selectedYear)}
                  onValueChange={(val) => setSelectedYear(Number(val))}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="w-24 h-8 text-xs bg-white">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_YEARS.map((yr) => (
                      <SelectItem key={yr} value={String(yr)}>
                        {yr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </DialogHeader>
            <div className="space-y-6 pt-2">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Total Employees</p>
                    <p className="text-2xl font-bold mt-1">{mockEmployees.length}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Gross Amount</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      ₹{(totalGross / 1000).toFixed(0)}K
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Net Payout</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">
                      ₹{(totalNet / 1000).toFixed(0)}K
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Gross</TableHead>
                      <TableHead>PF</TableHead>
                      <TableHead>ESI</TableHead>
                      <TableHead>TDS</TableHead>
                      <TableHead>Net Pay</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mockEmployees.map((emp) => (
                      <TableRow key={emp.id}>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <img src={emp.photo} alt={emp.name} className="w-8 h-8 rounded-full" />
                            <div>
                              <p className="font-medium">{emp.name}</p>
                              <p className="text-xs text-gray-500">{emp.id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-semibold">₹{emp.grossSalary.toLocaleString()}</TableCell>
                        <TableCell className="text-red-600">-₹{emp.pfContribution.toLocaleString()}</TableCell>
                        <TableCell className="text-red-600">-₹{emp.esiContribution.toLocaleString()}</TableCell>
                        <TableCell className="text-red-600">-₹{emp.tds.toLocaleString()}</TableCell>
                        <TableCell className="font-bold text-green-600">₹{emp.netSalary.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            Verified
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={handleSaveDraft} disabled={isSubmitting}>
                  Save as Draft
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleApproveAndDisburse}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Disbursing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Approve & Disburse
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">This Month Gross</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">₹{(totalGross / 1000).toFixed(0)}K</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">This Month Net</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">₹{(totalNet / 1000).toFixed(0)}K</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Deductions</p>
            <p className="text-2xl font-bold text-red-600 mt-1">₹{(totalDeductions / 1000).toFixed(0)}K</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Next Payroll</p>
            <p className="text-2xl font-bold text-purple-600 mt-1">Jan 28</p>
          </CardContent>
        </Card>
      </div>

      {/* Payroll History */}
      <Card>
        <CardHeader>
          <CardTitle>Payroll History</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" className="w-full">
            <TabsList>
              <TabsTrigger value="all">All Runs</TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
              <TabsTrigger value="pending">Pending</TabsTrigger>
            </TabsList>
            <TabsContent value="all" className="mt-4">
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pay Period</TableHead>
                      <TableHead>Employees</TableHead>
                      <TableHead>Gross Amount</TableHead>
                      <TableHead>Deductions</TableHead>
                      <TableHead>Net Amount</TableHead>
                      <TableHead>Disbursed On</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payrollRuns.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{run.month}</p>
                            <p className="text-sm text-gray-500">{run.period}</p>
                          </div>
                        </TableCell>
                        <TableCell>{run.totalEmployees}</TableCell>
                        <TableCell className="font-semibold">₹{run.grossAmount.toLocaleString()}</TableCell>
                        <TableCell className="text-red-600">-₹{run.totalDeductions.toLocaleString()}</TableCell>
                        <TableCell className="font-bold text-green-600">₹{run.netAmount.toLocaleString()}</TableCell>
                        <TableCell>{run.disbursedDate}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-600">
                            <CheckCircle className="mr-1 h-3 w-3" />
                            {run.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Payroll;
